/**
 * Persistantly storable objects. Use the inherited methods
 * from Foundation.Observable to get and set the persistent
 * data. Please note: you should never call this constructor
 * directly, and you should not make subclasses. See
 * Foundation.Storable.Bucket for how to get instances of
 * this class.
 * @constructor
 * @extends {Foundation.Observable}
 * @param {string} identifier
 */
Foundation.Storable = function (identifier) {
    Foundation.Observable.call(this);

    if (Foundation.Storable.Bucket.identifierIsUnsafe(identifier)) {
        throw new Error(`Attempted to initialize Foundation.Storable instance with non-alpha-numeric identifier: ${identifier}`)
    }

    /**
     * @private
     * @type {string}
     */
    this._storableIdentifier = identifier;

    /**
     * @private
     * @type {Foundation.Storable.Bucket}
     */
    this._defaultBucket = null;

    /**
     * @private
     * @type {number}
     */
    this._lastModifiedTime = 0;

    /**
     * @private
     * @type {number}
     */
    this._bucketInstanceId = 0;
}
Foundation.inherit(Foundation.Storable, Foundation.Observable);

/**
 * Shared session-persistent Bucket instance with the given namespace.
 * @public
 * @param {string} namespace
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Storable.sessionBucketForNamespace = function (namespace) {
    return Foundation.Storable.bucketForNamepace(
        namespace,
        Foundation.Storable.Bucket.Persistence.SESSION
    );
};

/**
 * Shared indefinite-persistent Bucket instance with the given namespace.
 * @public
 * @param {string} namespace
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Storable.indefiniteBucketForNamespace = function (namespace) {
    return Foundation.Storable.bucketForNamepace(
        namespace,
        Foundation.Storable.Bucket.Persistence.INDEFINITE
    );
};

/**
 * @private
 * @param {string} namespace
 * @param {Foundation.Storable.Bucket.Persistence} persistence
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Storable.bucketForNamepace = function (namespace, persistence) {
    if (!Foundation.Storable._sharedBuckets) {
        Foundation.Storable._sharedBuckets = [];
        Foundation.Storable._sharedBuckets[Foundation.Storable.Bucket.Persistence.SESSION] = {};
        Foundation.Storable._sharedBuckets[Foundation.Storable.Bucket.Persistence.INDEFINITE] = {};
    }

    if (!Foundation.Storable._sharedBuckets[persistence][namespace]) {
        Foundation.Storable._sharedBuckets[persistence][namespace] = 
            new Foundation.Storable.Bucket(namespace, persistence);
    }

    return Foundation.Storable._sharedBuckets[persistence][namespace];
};

/**
 * Unique identifier for this object within the bucket it will be
 * stored.
 * @public
 * @return {string}
 */
Foundation.Storable.prototype.storableIdentifier = function () {
    return this._storableIdentifier;
}

/**
 * Turns on autosaving for this instance. Whenever a value of
 * a field is updated, a call to save will be scheduled.
 * @return {Foundation.Storable}
 */
Foundation.Storable.prototype.autosave = function () {
    this.observeCoalesced("changed", function () { this.save() }.bind(this));
    return this;
}

/**
 * Save the storable's data to its default bucket.
 * @return {Foundation.Promise}
 */
Foundation.Storable.prototype.save = function () {
    var bucket = this.defaultBucket();
    if (!bucket) {
        var err = new Error("Default bucket never set on Foundation.Storable instance.");
        err["storableInstance"] = this;
        throw err;
    }
    return bucket.saveStorable(this);
}

/**
 * Return the default Foundation.Storable.Bucket instance that is used
 * when the save method is called.
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Storable.prototype.defaultBucket = function () {
    return this._defaultBucket;
}

/**
 * Sets the default Foundation.Storable.Bucket instance to use when the
 * save method is called.
 * @param {Foundation.Storable.Bucket} bucket
 * @return {Foundation.Storable}
 */
Foundation.Storable.prototype.setDefaultBucket = function (bucket) {
    this._defaultBucket = bucket;
    return this;
}

/**
 * Return a basic JSON-compatible object for persistent storage.
 * @return {Object.<string,*>}
 */
Foundation.Storable.prototype.storableData = function () {
    return this.toJSON();
}

/**
 * The number of seconds since this storable object was last saved to
 * a storable bucket.
 * @public
 * @return {number}
 */
Foundation.Storable.prototype.secondsSinceLastSave = function () {
    var nowSeconds = Math.floor(Foundation.currentTime() / 1000);
    return nowSeconds - this._lastModifiedTime;
};

/**
 * @param {number} seconds
 * @return {Foundation.Storable}
 */
Foundation.Storable.prototype.setLastSaveTime = function (seconds) {
    this._lastModifiedTime = seconds;
    return this;
};

/**
 * @return {number}
 */
Foundation.Storable.prototype.bucketInstanceId = function () {
    return this._bucketInstanceId;
}

/**
 * @param {number} id
 * @return {Foundation.Storable}
 */
Foundation.Storable.prototype.setBucketInstanceId = function (id) {
    this._bucketInstanceId = id;
    return this;
}

/**
 * @return {number}
 */
Foundation.Storable.prototype.lastSaveTime = function () {
    return this._lastModifiedTime;
};

window["Foundation"]["Storable"] = Foundation.Storable;
Foundation.Storable["sessionBucketForNamespace"] = Foundation.Storable.sessionBucketForNamespace;
Foundation.Storable["indefiniteBucketForNamespace"] = Foundation.Storable.indefiniteBucketForNamespace;
Foundation.Storable.prototype["storableIdentifier"] = Foundation.Storable.prototype.storableIdentifier;
Foundation.Storable.prototype["save"] = Foundation.Storable.prototype.save;
Foundation.Storable.prototype["autosave"] = Foundation.Storable.prototype.autosave;
Foundation.Storable.prototype["defaultBucket"] = Foundation.Storable.prototype.defaultBucket;
Foundation.Storable.prototype["setDefaultBucket"] = Foundation.Storable.prototype.setDefaultBucket;

